import { useEffect, useMemo, useState } from 'react';
import { JsonRpcSigner, Web3Provider } from '@ethersproject/providers';
import { isAddress } from '@ethersproject/address';
import { ethers } from 'ethers';
import { useWallet } from 'components/WalletContext';
import { checkDeployment } from 'utils/contractHelpers';
import {
  CVWearables,
  CVWearablesFactory,
  ERC20,
  ERC20Factory,
  Futura,
  FuturaFactory,
  MixpassSale,
  MixpassSaleFactory,
} from 'contracts';

export enum Contracts {
  MixpassSale = 'MixpassSale',
  ERC20 = 'ERC20',
  Futura = 'Futura',
  CVWearables = 'CVWearables'
}

const FACTORIES = {
  [Contracts.MixpassSale]: MixpassSaleFactory,
  [Contracts.ERC20]: ERC20Factory,
  [Contracts.Futura]: FuturaFactory,
  [Contracts.CVWearables]: CVWearablesFactory,
};

type KnownContracts = MixpassSale | ERC20 | Futura | CVWearables;

// account is not optional
function getSigner(provider: Web3Provider, account: string): JsonRpcSigner {
  return provider.getSigner(account).connectUnchecked();
}

// account is optional
function getProviderOrSigner(
  provider: Web3Provider,
  account?: string
): Web3Provider | JsonRpcSigner {
  return account ? getSigner(provider, account) : provider;
}

// account is optional
function getContract(
  address: string,
  contractName: Contracts,
  provider: Web3Provider,
  account?: string
): KnownContracts {
  if (!isAddress(address) || address === ethers.constants.AddressZero) {
    throw Error(`Invalid 'address' parameter '${address}'.`);
  }

  const contract = FACTORIES[contractName].connect(address, getProviderOrSigner(provider, account));
  return contract;
}

// returns null on errors
export function useContract(
  address: string | undefined,
  contractName: Contracts,
  networkId?: number
): KnownContracts | null {
  const { web3Provider, address: account, network } = useWallet();
  const [contract, setContract] = useState<KnownContracts | null>(null);

  // TODO: verify network is correct one for the specific contract?
  useEffect(() => {
    async function loadContract() {
      if (!address || !contractName || !web3Provider) return;
      if (network !== networkId) return;

      const c = getContract(address, contractName, web3Provider, account ? account : undefined);
      const isContract = await checkDeployment(web3Provider, address);
      if (!isContract) return;

      setContract(c);
    }

    if (address !== ethers.constants.AddressZero && networkId) {
      loadContract();
    }
  }, [address, contractName, account, web3Provider, networkId, network]);

  return useMemo(() => contract, [contract]);
}
